import { api } from './client';
import type { InstallerTask, PhotoItem } from '@/types/odoo';

// Interventie tasks have the same shape as installer tasks
export type InterventieTask = InstallerTask;

export async function fetchInterventieTasks(): Promise<InterventieTask[]> {
  const res = await api<{ tasks: InterventieTask[] }>('/api/interventie/tasks');
  return res.tasks;
}

export async function fetchProjectInterventiePhotos(projectId: number): Promise<PhotoItem[]> {
  const res = await api<{ photos: PhotoItem[] }>(`/api/interventie/project/${projectId}/photos`);
  return res.photos;
}

export async function uploadProjectInterventiePhoto(projectId: number, file: File): Promise<{ key: string }> {
  const formData = new FormData();
  formData.append('file', file);
  return api<{ key: string }>(`/api/interventie/project/${projectId}/photos`, {
    method: 'POST',
    body: formData,
  });
}

export async function deleteProjectInterventiePhoto(projectId: number, key: string): Promise<void> {
  await api(`/api/interventie/project/${projectId}/photos`, {
    method: 'DELETE',
    body: JSON.stringify({ key }),
  });
}

/**
 * Re-date a group of interventie photos to `date` (yyyy-mm-dd). The day lives in the
 * S3 key's filename prefix, so the server re-keys each photo (copy+delete). Returns the
 * old→new key mapping so the caller can re-key its local state without a full refetch.
 */
export async function redateProjectInterventiePhotos(
  projectId: number,
  keys: string[],
  date: string,
): Promise<{ moved: Array<{ oldKey: string; newKey: string }> }> {
  return api<{ moved: Array<{ oldKey: string; newKey: string }> }>(
    `/api/interventie/project/${projectId}/photos/date`,
    { method: 'PATCH', body: JSON.stringify({ keys, date }) },
  );
}

/* ────────────────────────────────────
   Per-task functions (the Interventie shell, the installer shell, and the
   dashboard's per-fase upload). The server resolves every path segment from
   Odoo: a new photo lands in the task's FASE folder, keyed on the fase id.
   ──────────────────────────────────── */
export async function uploadTaskPhoto(taskId: number, file: File): Promise<{ key: string }> {
  const formData = new FormData();
  formData.append('file', file);
  return api<{ key: string }>(`/api/photos/interventie/${taskId}`, {
    method: 'POST',
    body: formData,
  });
}

/** The task's own photos: its fase folder plus the older name-keyed folders. */
export async function listTaskPhotos(taskId: number): Promise<PhotoItem[]> {
  const res = await api<{ photos: PhotoItem[] }>(`/api/photos/interventie/${taskId}`);
  return res.photos;
}

export async function deleteTaskPhoto(taskId: number, key: string): Promise<void> {
  await api(`/api/photos/interventie/${taskId}`, {
    method: 'DELETE',
    body: JSON.stringify({ key }),
  });
}
